//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useQueryClient,
} from '@tanstack/react-query';
import React, { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  ContentLibrarySortType,
  IContentAggregateMediaStats,
  IContentLibraryFiltersOptions,
  IContentLibraryItem,
  IContentLibraryItemsResponse,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import { LibraryGridContent } from '@legacy-components/content/library-grid-content/LibraryGridContent';
import { MediaLibraryContentTopBar } from '@media-library/components/top-bar/MediaLibraryContentTopBar';
import { MediaLibrarySortType } from '@media-library/constants/mediaLibraryConstants';
import {
  getQueuedItemIds,
  getUploadQueueStatus,
} from '@media-library/redux/getMediaUploadQueue';
import { UploadQueueStatus } from '@media-library/redux/upload-queue/uploadQueueModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import {
  addSchedulerVideosFinishedProcessingListener,
  addUiaRemoveItemsFromLibraryListener,
} from '@react-bridge/wrappers/legacy-events';

const INFINITE_SCROLL_CONTAINER_ID =
  'library-content-infinite-scroll-container';

interface MediaLibraryContentProps {
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<IContentLibraryItemsResponse>>;
  filters?: IContentLibraryFiltersOptions;
  handleChangeSearchText: (searchText: string) => void;
  handleChangeSortType: (sortType: MediaLibrarySortType) => void;
  handleChangeSources: (sources: IContentLibrarySourcesOptions) => void;
  handleToggleFilterPanel: () => void;
  hasNextPage: boolean | undefined;
  initialSearchText: string;
  isLoading: boolean;
  isRefreshingContent: boolean;
  itemCount: number;
  items: IContentLibraryItem[];
  itemStats?: IContentAggregateMediaStats;
  orderBy?: ContentLibrarySortType;
  showSummary?: boolean;
  showSummaryToggle?: ReactNode;
  sortType: MediaLibrarySortType;
  sources: IContentLibrarySourcesOptions;
}

export const MediaLibraryContent = ({
  fetchNextPage,
  filters,
  handleChangeSearchText,
  handleChangeSortType,
  handleChangeSources,
  handleToggleFilterPanel,
  hasNextPage,
  initialSearchText,
  isLoading,
  itemCount,
  itemStats,
  items,
  orderBy,
  showSummary,
  showSummaryToggle,
  sortType,
  sources,
}: MediaLibraryContentProps) => {
  const queryClient = useQueryClient();
  const queuedItemIds = useSelector(getQueuedItemIds);
  const queueStatus = useSelector(getUploadQueueStatus);

  const invalidateMediaItems = () => {
    void queryClient.invalidateQueries({
      queryKey: [
        QueryKeyConstants.MediaFilters,
        QueryKeyConstants.MediaFiltersMediaItems,
      ],
    });
  };

  // Listen to 'uia:remove-items-from-library' event and invalidate media items when items are removed from library.
  useEffect(() => {
    addUiaRemoveItemsFromLibraryListener(() => invalidateMediaItems());
  }, []);

  // Listen to 'scheduler:videos-finished-processing' event and refetch media items when videos finished processing.
  useEffect(() => {
    addSchedulerVideosFinishedProcessingListener(() => {
      invalidateMediaItems();
    });
  }, []);

  useEffect(() => {
    if (queueStatus === UploadQueueStatus.Success) {
      invalidateMediaItems();
    }
  }, [queueStatus, queuedItemIds, queryClient]);

  return (
    <Container id={INFINITE_SCROLL_CONTAINER_ID}>
      <MediaLibraryContentTopBar
        filters={filters}
        handleChangeSearchText={handleChangeSearchText}
        handleChangeSortType={handleChangeSortType}
        handleChangeSources={handleChangeSources}
        handleToggleFilterPanel={handleToggleFilterPanel}
        initialSearchText={initialSearchText}
        isLoading={isLoading}
        itemCount={itemCount}
        itemStats={itemStats}
        orderBy={orderBy}
        showSummary={showSummary}
        showSummaryToggle={showSummaryToggle}
        sortType={sortType}
        sources={sources}
      />

      <LibraryGridContent
        endOfItemsReached={!hasNextPage}
        fetchMoreContent={fetchNextPage}
        isLoadingInitialContent={isLoading}
        isRefreshingContent={isLoading}
        items={items}
        queuedUploads={queuedItemIds}
        scrollContainer={`#${INFINITE_SCROLL_CONTAINER_ID}`}
        viewMode={'meta'}
      />
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${theme.spacings[3]};
  padding: ${theme.spacings[4]};
  overflow-y: auto;
`,
);
